import { useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  EntityId,
  EntitySelectEntity,
  EntitySelectSubEntity,
} from '@factorialco/f0-react/dist/experimental'
import { uniqBy } from 'lodash'

import { EntitySelectWrapper as EntitySelector } from 'modules/core/components/EntitySelectWrapper'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import useSubscribe from 'modules/core/hooks/useSubscribe'
import i18n from 'modules/core/lib/i18n'
import { StrippedEvent } from 'modules/core/lib/pubSub/types'

import { BaseEntitySelectorWrapper } from 'modules/finance/components/FinancialWorkspace/components/Filters/BaseEntitySelectorWrapper'
import { useAvailabilities } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { usePermissionsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'

import { PAYROLL_SOURCES_EVENT } from './const'
import {
  DATA_SOURCE_GROUPS,
  DATA_SOURCE_GROUPS_KEYS,
  ENTITIES,
  GROUPS,
  GROUP_VALUES,
  dataSourceGroups,
} from './data/groups'
import { useData } from './hooks/useData'
import { GroupType, SelectedSource } from './types'

interface DataSourceSelectorProps {
  defaultOpen?: boolean
  defaultSelectedSources?: EntitySelectEntity[]
  onChange: (value: EntitySelectEntity[] | null, groupedValues: SelectedSource) => void
}

export const DataSourceSelector = ({
  defaultOpen,
  defaultSelectedSources,
  onChange,
}: DataSourceSelectorProps) => {
  const { data, loading } = useData()
  const { availabilities } = useAvailabilities()
  const { permissions } = usePermissionsData()

  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [selectedSources, setSelectedSources] = useState<SelectedSource>({
    agreement_data: [],
    employee_expenses: [],
    labor_costs: [],
  })

  const hasInitialized = useRef(false)

  useSubscribe([PAYROLL_SOURCES_EVENT], (event: StrippedEvent) => {
    const entities = ENTITIES({
      expandedElements,
      availabilities,
      permissions,
      subItems: {
        agreement_data: selectedSources.agreement_data ?? [],
        employee_expenses: selectedSources.employee_expenses ?? [],
        labor_costs: [
          { subId: `labor_costs_${event.data?.concept.id}`, subName: event.data?.concept.name },
        ],
      },
    })

    handleOnSelectMultiple(Object.values(entities).filter((entity) => entity.enabled))
  })

  const selectionIsGroup = useUnsafeCallback((entityId: EntityId): entityId is GroupType => {
    return DATA_SOURCE_GROUPS_KEYS.includes(String(entityId))
  }, [])

  const entry = useUnsafeCallback(
    (group: string, subItems: EntitySelectSubEntity[]) => {
      if (selectionIsGroup(group)) {
        return {
          id: group,
          name: DATA_SOURCE_GROUPS[group],
          subItems,
        }
      }

      return undefined
    },
    [selectionIsGroup]
  )

  const selectedGroupedEntities: EntitySelectEntity[] = useUnsafeMemo(() => {
    const entries = Object.entries(selectedSources)

    return entries.map(([group, subItems]) => entry(group, subItems)).filter(Boolean)
  }, [entry, selectedSources])

  const findSelection = (selection: EntitySelectEntity[] | null, group: GroupType) => {
    return selection?.find((entity) => entity.id === group)
  }

  const handleOnSelectMultiple = useUnsafeCallback(
    (selection: EntitySelectEntity[] | null) => {
      const newSelectedSources = { ...selectedSources }

      Object.keys(newSelectedSources).forEach((group) => {
        if (selectionIsGroup(group)) {
          const selectionToAssign = findSelection(selection, group)

          newSelectedSources[group] = selectionToAssign?.subItems ?? []
        }
      })

      setSelectedSources({
        agreement_data: uniqBy(newSelectedSources.agreement_data, 'subId'),
        employee_expenses: uniqBy(newSelectedSources.employee_expenses, 'subId'),
        labor_costs: uniqBy(newSelectedSources.labor_costs, 'subId'),
      })

      onChange(selection, newSelectedSources)
    },
    [onChange, selectedSources, selectionIsGroup]
  )

  const selectedCount = useUnsafeMemo(() => {
    return Object.values(selectedSources).flat().length
  }, [selectedSources])

  useUnsafeEffect(() => {
    if (defaultSelectedSources && !hasInitialized.current) {
      hasInitialized.current = true
      handleOnSelectMultiple(defaultSelectedSources)
    }
  }, [defaultSelectedSources, handleOnSelectMultiple])

  return (
    <Box flexNoShrink>
      <EntitySelector
        label={i18n.t('finance.financial_workspace.employee_costs.data_source_selector.title')}
        hideLabel
        multiple
        entities={dataSourceGroups(expandedElements, data, availabilities, permissions)}
        singleSelector={false}
        groups={GROUPS}
        selectedGroup={GROUP_VALUES.ALL}
        selectedGroupedEntities={selectedGroupedEntities}
        onGroupChange={() => {}}
        onSelect={() => {}} // We're using onSelectMultiple instead
        onSelectExpandedElements={setExpandedElements}
        onSelectMultiple={handleOnSelectMultiple}
        placeholder={i18n.t(
          'finance.financial_workspace.employee_costs.data_source_selector.placeholder'
        )}
        selectedItemsCopy={i18n.t(
          'finance.financial_workspace.employee_costs.data_source_selector.placeholder'
        )}
        notFoundTitle={i18n.t(
          'finance.financial_workspace.employee_costs.data_source_selector.not_found_title'
        )}
        notFoundSubtitle={i18n.t(
          'finance.financial_workspace.employee_costs.data_source_selector.not_found_title'
        )}
        expandedElements={expandedElements}
        selectAllLabel={i18n.t('employee_selector.select_all')}
        clearLabel={i18n.t('employee_selector.clear')}
        hiddenAvatar
        loading={loading}
        defaultOpen={defaultOpen}
      >
        <BaseEntitySelectorWrapper
          selected={selectedCount > 0}
          placeholder={i18n.t(
            'finance.financial_workspace.employee_costs.data_source_selector.title'
          )}
          selectedTitle={i18n.t(
            'finance.financial_workspace.employee_costs.data_source_selector.title'
          )}
          selectedCount={selectedCount}
        />
      </EntitySelector>
    </Box>
  )
}
